import DOMPurify from 'dompurify';
import { safeHref } from './safeHref';

const ALLOWED_TAGS = [
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'img',
  'li',
  'ol',
  'p',
  'pre',
  's',
  'span',
  'strong',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
];

const ALLOWED_ATTR = ['alt', 'class', 'href', 'rel', 'src', 'target', 'title'];

function applySafeUri(_node, data) {
  if (data.attrName !== 'href' && data.attrName !== 'src') return;
  const next = safeHref(data.attrValue);
  if (!next) {
    data.keepAttr = false;
    return;
  }
  data.attrValue = next;
}

/**
 * Blank-target anchors must not keep window.opener. Merge onto any
 * existing rel tokens and drop opener so a stored rel cannot undo it.
 */
function applyBlankTargetRel(node) {
  if (!node || node.nodeName !== 'A') return;
  if (node.getAttribute('target') !== '_blank') return;
  const tokens = String(node.getAttribute('rel') || '')
    .split(/\s+/)
    .filter((token) => token && token !== 'opener');
  if (!tokens.includes('noopener')) tokens.push('noopener');
  if (!tokens.includes('noreferrer')) tokens.push('noreferrer');
  node.setAttribute('rel', tokens.join(' '));
}

// Own instance so these hooks never leak into other DOMPurify callers.
const purify = DOMPurify(window);
purify.addHook('uponSanitizeAttribute', applySafeUri);
purify.addHook('afterSanitizeAttributes', applyBlankTargetRel);

/**
 * Sanitize marked HTML before v-html. Strips script/iframe/object,
 * event handlers, and javascript:/data:/protocol-relative hrefs.
 * URI policy is safeHref — same helper page nodes use. Blank-target
 * anchors always get rel="noopener noreferrer".
 *
 * @param {string} html
 * @returns {string}
 */
export function sanitizeRenderedHtml(html) {
  if (!html) return '';
  return purify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_ATTR: ['srcdoc'],
  });
}

const SVG_DANGEROUS_TAGS = ['script', 'iframe'];
const SVG_URI_ATTRS = ['href', 'xlink:href', 'src'];
const SVG_DANGEROUS_URI = /^(?:javascript|vbscript|data):/i;

// Drop whitespace and C0 control chars (code <= 0x20) so a scheme hidden as
// `java\tscript:` can't slip past SVG_DANGEROUS_URI. Done by char code rather
// than a control-char regex literal, which trips eslint's no-control-regex.
function stripUriControls(value) {
  let out = '';
  for (const ch of String(value)) {
    if (ch.charCodeAt(0) > 0x20) {
      out += ch;
    }
  }
  return out;
}

/**
 * Sanitize a mermaid-rendered SVG string before it is assigned to innerHTML.
 *
 * @param {string} svg
 * @returns {string}
 */
export function sanitizeRenderedSvg(svg) {
  if (!svg) return '';
  const doc = new DOMParser().parseFromString(String(svg), 'text/html');
  const svgEl = doc.body.querySelector('svg');
  if (!svgEl) return '';

  for (const el of [svgEl, ...svgEl.querySelectorAll('*')]) {
    const tag = (el.tagName || '').toLowerCase();
    if (SVG_DANGEROUS_TAGS.includes(tag)) {
      el.remove();
      continue;
    }
    for (const attr of Array.from(el.attributes || [])) {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) {
        el.removeAttribute(attr.name);
        continue;
      }
      const uri = stripUriControls(attr.value);
      if (SVG_URI_ATTRS.includes(name) && SVG_DANGEROUS_URI.test(uri)) {
        el.removeAttribute(attr.name);
      }
    }
  }

  return svgEl.outerHTML;
}
